/**
 * Staffing plan from a live resource-histogram remaining curve.
 * Keep in sync with src/services/staffingPlan.js
 */

export const DEFAULT_RADIUS = 3;

function round2(n) {
  return Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
}

function asNumberOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function centeredAverage(values, index, radius = DEFAULT_RADIUS) {
  return centeredAverageRange(values, index, radius, 0, values.length - 1);
}

/** Excel AVERAGE bounded to [lo, hi] — the window never crosses the data date. */
export function centeredAverageRange(values, index, radius, lo, hi) {
  const r = Math.max(0, Number(radius) || 0);
  const a = Math.max(Math.max(0, lo), index - r);
  const b = Math.min(Math.min(values.length - 1, hi), index + r);
  let sum = 0;
  let n = 0;
  for (let i = a; i <= b; i++) {
    const v = asNumberOrNull(values[i]);
    if (v == null) continue;
    sum += v;
    n += 1;
  }
  return n ? sum / n : 0;
}

export function smoothSeries(values, radius = DEFAULT_RADIUS) {
  return (values || []).map((_, i) => centeredAverage(values, i, radius));
}

export function hoursToMen(hours, weekHrs) {
  const hpw = Number(weekHrs);
  if (!Number.isFinite(hpw) || hpw <= 0) return 0;
  return (Number(hours) || 0) / hpw;
}

export function roundMen(n) {
  return Math.round((Number(n) || 0) + Number.EPSILON);
}

export function hireLayoff(men) {
  return (men || []).map((v, i) => {
    const cur = Number(v) || 0;
    const prev = i === 0 ? 0 : (Number(men[i - 1]) || 0);
    return cur - prev;
  });
}

export function firstLastNonZero(values, eps = 0.0001) {
  let first = -1;
  let last = -1;
  for (let i = 0; i < values.length; i++) {
    if (Math.abs(Number(values[i]) || 0) > eps) {
      if (first < 0) first = i;
      last = i;
    }
  }
  return { first, last };
}

export const SAFETY_RULE_EVERY = 'every';
export const SAFETY_RULE_FIRST_THEN = 'firstThen';
/** First at Y, the next N men later, then one more every T. */
export const SAFETY_RULE_THREE = 'firstThenFinal';
export const DEFAULT_MEN_PER_SAFETY = 25;
export const DEFAULT_SAFETY_FIRST_AT = 20;
export const DEFAULT_SAFETY_THEN_EVERY = 40;
export const DEFAULT_SAFETY_FINAL_EVERY = 50;

function safetyStep(value, fallback) {
  const n = Math.round(Number(value));
  if (Number.isFinite(n) && n >= 1) return n;
  const fb = Math.round(Number(fallback));
  return Number.isFinite(fb) && fb >= 1 ? fb : 1;
}

/** @param {number|{mode?:string,menPer?:number,menPerSafety?:number,firstAt?:number,thenEvery?:number,finalEvery?:number}} input */
export function normalizeSafetyRule(input) {
  if (input && typeof input === 'object') {
    const mode = input.mode === SAFETY_RULE_FIRST_THEN
      ? SAFETY_RULE_FIRST_THEN
      : input.mode === SAFETY_RULE_THREE
        ? SAFETY_RULE_THREE
        : SAFETY_RULE_EVERY;
    return {
      mode,
      menPer: safetyStep(input.menPer ?? input.menPerSafety, DEFAULT_MEN_PER_SAFETY),
      firstAt: safetyStep(input.firstAt, DEFAULT_SAFETY_FIRST_AT),
      thenEvery: safetyStep(input.thenEvery, DEFAULT_SAFETY_THEN_EVERY),
      finalEvery: safetyStep(input.finalEvery, DEFAULT_SAFETY_FINAL_EVERY)
    };
  }
  return {
    mode: SAFETY_RULE_EVERY,
    menPer: safetyStep(input, DEFAULT_MEN_PER_SAFETY),
    firstAt: DEFAULT_SAFETY_FIRST_AT,
    thenEvery: DEFAULT_SAFETY_THEN_EVERY,
    finalEvery: DEFAULT_SAFETY_FINAL_EVERY
  };
}

/** Crew size where safety #2 starts on the three-step rule. */
function safetyThreeSecondAt(rule) {
  return rule.firstAt + rule.thenEvery;
}

export function safetyCountAt(men, ruleInput) {
  const rule = normalizeSafetyRule(ruleInput);
  const n = Number(men) || 0;
  if (n <= 0) return 0;
  if (rule.mode === SAFETY_RULE_THREE) {
    const secondAt = safetyThreeSecondAt(rule);
    if (n < rule.firstAt) return 0;
    if (n < secondAt) return 1;
    return 2 + Math.floor((n - secondAt) / rule.finalEvery);
  }
  if (rule.mode === SAFETY_RULE_FIRST_THEN) {
    if (n < rule.firstAt) return 0;
    return 1 + Math.floor((n - rule.firstAt) / rule.thenEvery);
  }
  return Math.ceil(n / rule.menPer);
}

export function safetyThresholdFor(n, ruleInput) {
  const rule = normalizeSafetyRule(ruleInput);
  const k = Math.max(1, Math.round(Number(n) || 1));
  if (rule.mode === SAFETY_RULE_THREE) {
    if (k === 1) return rule.firstAt;
    const secondAt = safetyThreeSecondAt(rule);
    if (k === 2) return secondAt;
    return secondAt + (k - 2) * rule.finalEvery;
  }
  if (rule.mode === SAFETY_RULE_FIRST_THEN) {
    return rule.firstAt + (k - 1) * rule.thenEvery;
  }
  return (k - 1) * rule.menPer;
}

export function safetyThresholdsUpTo(peak, ruleInput) {
  const rule = normalizeSafetyRule(ruleInput);
  const top = Number(peak) || 0;
  const out = [];
  if (rule.mode === SAFETY_RULE_THREE) {
    if (top < rule.firstAt) return out;
    out.push(rule.firstAt);
    const secondAt = safetyThreeSecondAt(rule);
    if (secondAt > top) return out;
    out.push(secondAt);
    for (let y = secondAt + rule.finalEvery; y <= top; y += rule.finalEvery) out.push(y);
    return out;
  }
  if (rule.mode === SAFETY_RULE_FIRST_THEN) {
    if (top < rule.firstAt) return out;
    for (let y = rule.firstAt; y <= top; y += rule.thenEvery) out.push(y);
    return out;
  }
  for (let y = rule.menPer; y <= top; y += rule.menPer) out.push(y);
  return out;
}

export function safetyLineLabelCount(y, ruleInput) {
  const rule = normalizeSafetyRule(ruleInput);
  if (rule.mode === SAFETY_RULE_FIRST_THEN || rule.mode === SAFETY_RULE_THREE) return safetyCountAt(y, rule);
  return Math.round(y / rule.menPer) + 1;
}

export function safetyCountWord(n) {
  const v = Math.max(0, Math.round(Number(n) || 0));
  return v === 1 ? '1 safety' : `${v} safeties`;
}

export function safetyRuleLabel(ruleInput) {
  const rule = normalizeSafetyRule(ruleInput);
  if (rule.mode === SAFETY_RULE_THREE) {
    return `first ${rule.firstAt}, +${rule.thenEvery}, then /${rule.finalEvery}`;
  }
  if (rule.mode === SAFETY_RULE_FIRST_THEN) {
    return `first ${rule.firstAt}, then /${rule.thenEvery}`;
  }
  return `1 per ${rule.menPer}`;
}

/** Plain-language rule, used on the plan popup and in exports. */
export function safetyRuleSummary(ruleInput) {
  const rule = normalizeSafetyRule(ruleInput);
  if (rule.mode === SAFETY_RULE_THREE) {
    const nextAt = safetyThreeSecondAt(rule);
    return `No safety under ${rule.firstAt} men. First safety at ${rule.firstAt}, the next at ${nextAt}, then one more every ${rule.finalEvery}.`;
  }
  if (rule.mode === SAFETY_RULE_FIRST_THEN) {
    return `No safety under ${rule.firstAt} men. First safety at ${rule.firstAt}, then one more every ${rule.thenEvery}.`;
  }
  return `One safety person per ${rule.menPer} workers.`;
}

export function safetyNeededWhen(threshold, ruleInput) {
  const rule = normalizeSafetyRule(ruleInput);
  if (rule.mode === SAFETY_RULE_FIRST_THEN || rule.mode === SAFETY_RULE_THREE) {
    return `Crew of ${threshold} or more`;
  }
  return threshold === 0 ? 'Any crew on site' : `Crew above ${threshold} men`;
}

/**
 * Apply include/exclude indirect + optional from/to to a weekly crew series
 * `{ date, men, indirect }`. Same numbers the Safety plan table uses.
 */
export function safetyCrewValues(crew, cfg = {}) {
  const includeIndirect = cfg.includeIndirect !== false;
  const from = cfg.from || '';
  const to = cfg.to || '';
  return (crew || [])
    .map((p) => ({
      date: String(p?.date || '').slice(0, 10),
      men: Math.max(0, (Number(p?.men) || 0) - (includeIndirect ? 0 : (Number(p?.indirect) || 0)))
    }))
    .filter((p) => p.date && (!from || p.date >= from) && (!to || p.date <= to));
}

/**
 * Safety coverage from a weekly crew series.
 * Every-N (default): Safety #k when crew > (k − 1) × N.
 * First-then: none below firstAt; #1 at firstAt; then one more every thenEvery
 * (default 20 then 40).
 * Three-step: #1 at firstAt, #2 at firstAt + thenEvery (that step once),
 * then one more every finalEvery (default 20, +40, then every 50).
 * A number argument is still every-N.
 * Returns [{ n, threshold, from, to, weeks, peak }].
 */
export function safetyWindows(weeks, men, ruleOrPer = 25) {
  const rule = normalizeSafetyRule(ruleOrPer);
  const vals = (weeks || []).map((w, i) => ({
    date: String(w).slice(0, 10),
    men: Number(men?.[i]) || 0
  }));
  const peak = vals.reduce((m, v) => Math.max(m, v.men), 0);
  const out = [];
  const count = safetyCountAt(peak, rule);
  const inclusive = rule.mode === SAFETY_RULE_FIRST_THEN || rule.mode === SAFETY_RULE_THREE;
  for (let n = 1; n <= count; n += 1) {
    const threshold = safetyThresholdFor(n, rule);
    const above = vals.filter((v) => (inclusive ? v.men >= threshold : v.men > threshold));
    if (!above.length) continue;
    out.push({
      n,
      threshold,
      from: above[0].date,
      to: above[above.length - 1].date,
      weeks: above.length,
      peak: above.reduce((m, v) => Math.max(m, v.men), 0)
    });
  }
  return out;
}

export const DEFAULT_MEN_PER_FM = 8;
export const DEFAULT_FM_PER_GF = 3;

/** How many of a role you need for a weekly headcount (same ceil rule as safety). */
export function neededAt(men, per, fallbackPer = DEFAULT_MEN_PER_FM) {
  const p = Math.max(1, Math.round(Number(per) || fallbackPer));
  const v = Number(men) || 0;
  if (v <= 0) return 0;
  return Math.ceil(v / p);
}

/**
 * GF is a FLOOR rule within one trade: no GF under `menPerGf` men in that
 * trade — the first GF starts at 24 men (8 men/FM × 3 FMs/GF), the next at 48.
 */
export function gfNeededAt(men, menPerGf) {
  const p = Math.max(1, Math.round(Number(menPerGf) || DEFAULT_MEN_PER_FM * DEFAULT_FM_PER_GF));
  const v = Number(men) || 0;
  return v >= p ? Math.floor(v / p) : 0;
}

/** Horizontal FM / GF thresholds for the histogram + staffing crew chart. */
export function supervisionThresholds(peak, menPerFm = DEFAULT_MEN_PER_FM, fmPerGf = DEFAULT_FM_PER_GF) {
  const perFm = Math.max(1, Math.round(Number(menPerFm) || DEFAULT_MEN_PER_FM));
  const perGf = Math.max(1, Math.round(Number(fmPerGf) || DEFAULT_FM_PER_GF));
  const gfMen = perFm * perGf;
  const max = Number(peak) || 0;
  const fm = [];
  for (let y = perFm; y <= max; y += perFm) fm.push(y);
  const gf = [];
  for (let y = gfMen; y <= max; y += gfMen) gf.push(y);
  return { fm, gf, perFm, perGf, gfMen };
}

/** One selected resource can override the job-wide FM / GF ratios. */
export function supervisionRatiosForResources(resources, menPerFm, fmPerGf, overrides = {}) {
  const list = Array.isArray(resources) ? resources.filter(Boolean) : [];
  if (list.length === 1) {
    const id = list[0].resourceId || list[0].id;
    const ov = id ? overrides[id] : null;
    return {
      menPerFm: ov?.menPerFm != null ? ov.menPerFm : menPerFm,
      fmPerGf: ov?.fmPerGf != null ? ov.fmPerGf : fmPerGf
    };
  }
  return { menPerFm, fmPerGf };
}

/** Windows for the GF floor rule: GF #n is needed in weeks where crew ≥ n × per. */
export function floorWindows(weeks, men, per) {
  const p = Math.max(1, Math.round(Number(per) || 1));
  const vals = (weeks || []).map((w, i) => ({
    date: String(w).slice(0, 10),
    men: Number(men?.[i]) || 0
  }));
  const peak = vals.reduce((m, v) => Math.max(m, v.men), 0);
  const out = [];
  const count = Math.floor(peak / p);
  for (let n = 1; n <= count; n += 1) {
    const threshold = n * p;
    const above = vals.filter((v) => v.men >= threshold);
    if (!above.length) continue;
    out.push({
      n,
      threshold,
      basis: 'floor',
      from: above[0].date,
      to: above[above.length - 1].date,
      weeks: above.length,
      peak: above.reduce((m, v) => Math.max(m, v.men), 0)
    });
  }
  return out;
}

/** Windows from an already-counted weekly series (sum of per-trade needs): #n where count ≥ n. */
export function countWindows(weeks, counts) {
  const vals = (weeks || []).map((w, i) => ({
    date: String(w).slice(0, 10),
    c: Number(counts?.[i]) || 0
  }));
  const peak = vals.reduce((m, v) => Math.max(m, v.c), 0);
  const out = [];
  for (let n = 1; n <= peak; n += 1) {
    const above = vals.filter((v) => v.c >= n);
    if (!above.length) continue;
    out.push({
      n,
      threshold: n,
      basis: 'count',
      from: above[0].date,
      to: above[above.length - 1].date,
      weeks: above.length,
      peak: above.reduce((m, v) => Math.max(m, v.c), 0)
    });
  }
  return out;
}

/** 1 FM per 8 men ≈ 12.5% of crew. */
export function ratioToPercent(menPer) {
  const p = Math.max(1, Number(menPer) || 1);
  return Math.round((100 / p) * 10) / 10;
}

export function percentToRatio(pct, fallback = DEFAULT_MEN_PER_FM) {
  const n = Number(pct);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.max(1, Math.round(100 / n));
}

function clipWeekIndex(weeks, from, to) {
  return (weeks || [])
    .map((w, i) => ({ w: String(w).slice(0, 10), i }))
    .filter(({ w }) => (!from || w >= from) && (!to || w <= to));
}

function menSeriesFor(r, menByResource) {
  if (menByResource instanceof Map) return menByResource.get(r.resourceId) || [];
  return menByResource?.[r.resourceId] || r.curMen || [];
}

/**
 * Foreman / general-foreman plan from the staffing curve.
 * SUPERVISION IS PER TRADE (P6 resource ID) — never one ratio on the whole job:
 *   FM: 1 per `menPerFm` men within the trade (ceil, same rule as Safety).
 *   GF: floor rule within the trade — no GF under `menPerFm × fmPerGf` men
 *       (24 by default); the first GF starts at 24 men IN ONE trade, next at 48.
 * "overall" is the week-by-week SUM of the per-trade needs (a GF is never
 * shared across trades). P6 indirect IDs are always skipped — FMs and GFs ARE
 * the indirects, computed from the direct crew.
 */
export function buildSupervisionPlan({
  weeks = [],
  resources = [],
  menByResource,
  from = '',
  to = '',
  menPerFm = DEFAULT_MEN_PER_FM,
  fmPerGf = DEFAULT_FM_PER_GF,
  overrides = {}
} = {}) {
  const perFm = Math.max(1, Math.round(Number(menPerFm) || DEFAULT_MEN_PER_FM));
  const perGf = Math.max(1, Math.round(Number(fmPerGf) || DEFAULT_FM_PER_GF));
  const clipped = clipWeekIndex(weeks, from, to);
  const weekDates = clipped.map((c) => c.w);
  const idx = clipped.map((c) => c.i);

  const pickMen = (r) => {
    const all = menSeriesFor(r, menByResource);
    return idx.map((i) => Number(all[i]) || 0);
  };

  const byResource = (resources || [])
    .filter((r) => familyFromResource(r) !== 'indirect')
    .map((r) => {
      const men = pickMen(r);
      const ov = overrides?.[r.resourceId] || {};
      const rFm = Math.max(1, Math.round(Number(ov.menPerFm) || perFm));
      const rGf = Math.max(1, Math.round(Number(ov.fmPerGf) || perGf));
      const gfMenPer = rFm * rGf; // first GF at this many men in the trade
      const fm = men.map((m) => neededAt(m, rFm));
      const gf = men.map((m) => gfNeededAt(m, gfMenPer));
      const peakMen = men.reduce((m, v) => Math.max(m, v), 0);
      return {
        resourceId: r.resourceId,
        label: r.label || r.resourceName || r.resourceKey || r.resourceId,
        discipline: r.discipline,
        shopOrField: r.shopOrField,
        menPerFm: rFm,
        fmPerGf: rGf,
        gfMenPer,
        overridden: ov.menPerFm != null || ov.fmPerGf != null,
        men,
        fm,
        gf,
        peakMen,
        peakFm: fm.reduce((m, v) => Math.max(m, v), 0),
        peakGf: gf.reduce((m, v) => Math.max(m, v), 0),
        fmWindows: safetyWindows(weekDates, men, rFm),
        gfWindows: floorWindows(weekDates, men, gfMenPer)
      };
    })
    .filter((row) => row.peakMen > 0);

  // All-trades view = the sum of each trade's own needs, week by week.
  const sumSeries = (key) => weekDates.map((_, wi) => byResource
    .reduce((s, r) => s + (Number(r[key][wi]) || 0), 0));
  const overallMen = sumSeries('men');
  const overallFm = sumSeries('fm');
  const overallGf = sumSeries('gf');

  return {
    weeks: weekDates,
    menPerFm: perFm,
    fmPerGf: perGf,
    gfMenPer: perFm * perGf,
    overall: {
      men: overallMen,
      fm: overallFm,
      gf: overallGf,
      peakMen: overallMen.reduce((m, v) => Math.max(m, v), 0),
      peakFm: overallFm.reduce((m, v) => Math.max(m, v), 0),
      peakGf: overallGf.reduce((m, v) => Math.max(m, v), 0),
      fmWindows: countWindows(weekDates, overallFm),
      gfWindows: countWindows(weekDates, overallGf)
    },
    byResource,
    sumPeakFm: byResource.reduce((s, r) => s + r.peakFm, 0),
    sumPeakGf: byResource.reduce((s, r) => s + r.peakGf, 0)
  };
}

export function familyFromResource(meta = {}) {
  const disc = String(meta.discipline || '').toLowerCase();
  const shop = meta.shopOrField === 'shop';
  const isPipe = disc.includes('piping') || disc.includes('pipe');
  const isFab = shop || disc.includes('fab');
  if (disc === 'pipe_fab' || disc === 'pfs' || (isPipe && isFab)) return 'pipe_fab';
  if (isPipe) return 'piping';
  if (disc.includes('electrical') || disc === 'ele' || disc === 'ei') return 'electrical';
  if (disc.includes('mechanical') || disc.includes('millwright') || disc === 'mwr' || disc === 'mw') return 'mechanical';
  if (disc.includes('indirect')) return 'indirect';
  return disc || 'other';
}

const EX_ID_TOKEN_RE = /(?:^|[-_\/])ex(?:[-_\/]|$)/i;
const EXTRA_NAME_RE = /extra|additional\s*scope/i;

export function isExtraResource(meta = {}) {
  if (meta?.isExtra) return true;
  if (typeof meta === 'string') {
    return EX_ID_TOKEN_RE.test(meta) || EXTRA_NAME_RE.test(meta);
  }
  const id = [meta.resourceId, meta.rsrc_short_name, meta.rsrcShortName]
    .filter((s) => typeof s === 'string')
    .join('\n');
  const name = [meta.resourceName, meta.rsrc_name, meta.rsrcName]
    .filter((s) => typeof s === 'string')
    .join('\n');
  return EX_ID_TOKEN_RE.test(id) || EXTRA_NAME_RE.test(name);
}

function namedDisciplinesAllSelected(discs, allKnown) {
  const named = (allKnown || []).filter((id) => id && id !== '__none__');
  return named.length > 0 && named.every((id) => discs.includes(id));
}

export function resourceMatchesFilters(meta, filters = {}) {
  const discs = filters.disciplines;
  if (Array.isArray(discs) && discs.length) {
    const allKnown = filters.allDisciplines;
    if (!allKnown || discs.length < allKnown.length) {
      if (!discs.includes(meta.discipline)) {
        // Second XER has no Discipline activity code: suffix/name classify,
        // not (no value). If every named code is on, keep those hours.
        const uncoded = meta.disciplineSource !== 'udf';
        if (!(uncoded && namedDisciplinesAllSelected(discs, allKnown))) return false;
      }
    }
  }
  const fams = filters.families;
  if (Array.isArray(fams) && fams.length) {
    const allFams = filters.allFamilies;
    const active = fams.filter((f) => f && f !== 'overall');
    if (active.length && (!allFams || active.length < allFams.filter((f) => f !== 'overall').length)) {
      if (!active.includes(familyFromResource(meta))) return false;
    }
  }
  const shopField = filters.shopField;
  if (shopField === 'shop' || shopField === 'field') {
    const isShop = meta.shopOrField === 'shop' || familyFromResource(meta) === 'pipe_fab';
    if (shopField === 'shop' && !isShop) return false;
    if (shopField === 'field' && isShop) return false;
  }
  const scope = filters.scope;
  if (scope === 'extra' || scope === 'baseline') {
    const extra = isExtraResource(meta);
    if (scope === 'extra' && !extra) return false;
    if (scope === 'baseline' && extra) return false;
  }
  const ids = filters.resourceIds;
  if (Array.isArray(ids)) {
    const rid = meta.resourceId || meta.id;
    if (!ids.includes(rid) && !ids.includes(meta.resourceKey)) return false;
  }
  return true;
}

function collectWeeks(curve) {
  if (Array.isArray(curve?.weeks) && curve.weeks.length) {
    return curve.weeks.map((w) => String(w).slice(0, 10));
  }
  const set = new Set();
  Object.values(curve?.byResource || {}).forEach((r) => {
    (r.series || []).forEach((p) => { if (p?.date) set.add(String(p.date).slice(0, 10)); });
  });
  Object.values(curve?.byDiscipline || {}).forEach((weekMap) => {
    Object.keys(weekMap || {}).forEach((w) => set.add(String(w).slice(0, 10)));
  });
  (curve?.totals || []).forEach((t) => { if (t?.date) set.add(String(t.date).slice(0, 10)); });
  return [...set];
}

function curveByResource(curve) {
  if (curve?.byResource && Object.keys(curve.byResource).length) return curve.byResource;
  const out = {};
  Object.entries(curve?.byDiscipline || {}).forEach(([disc, weekMap]) => {
    const series = Object.entries(weekMap || {}).map(([date, hours]) => ({
      date: String(date).slice(0, 10),
      period: Number(hours) || 0
    })).sort((a, b) => a.date.localeCompare(b.date));
    if (!series.length) return;
    out[`actual:${disc}`] = {
      resourceId: `actual:${disc}`,
      resourceKey: String(disc).slice(0, 4).toUpperCase(),
      resourceName: `Spent · ${disc}`,
      label: `Spent · ${disc}`,
      discipline: disc,
      shopOrField: 'field',
      series
    };
  });
  if (!Object.keys(out).length && (curve?.totals || []).length) {
    out['actual:all'] = {
      resourceId: 'actual:all',
      resourceKey: 'Spent',
      resourceName: 'Spent',
      label: 'Spent',
      discipline: 'unknown',
      shopOrField: 'field',
      series: curve.totals.map((t) => ({ date: String(t.date).slice(0, 10), period: Number(t.period) || 0 }))
    };
  }
  return out;
}

function indexResources(byResource) {
  const map = new Map();
  Object.entries(byResource || {}).forEach(([id, rec]) => {
    map.set(id, rec);
    if (rec?.resourceId) map.set(rec.resourceId, rec);
  });
  return map;
}

function combineActualCurves(actuals) {
  const curves = (Array.isArray(actuals) ? actuals : [actuals]).filter(Boolean);
  const combined = {};
  const weekSet = new Set();
  curves.forEach((curve) => {
    const by = curveByResource(curve);
    collectWeeks({ ...curve, byResource: by }).forEach((w) => weekSet.add(w));
    Object.entries(by).forEach(([id, rec]) => {
      const rid = rec.resourceId || id;
      if (!combined[rid]) {
        combined[rid] = { ...rec, resourceId: rid, series: [...(rec.series || [])] };
        return;
      }
      const map = new Map((combined[rid].series || []).map((p) => [String(p.date).slice(0, 10), Number(p.period) || 0]));
      (rec.series || []).forEach((p) => {
        const d = String(p.date).slice(0, 10);
        if (!map.has(d) || !map.get(d)) map.set(d, Number(p.period) || 0);
      });
      combined[rid].series = [...map.entries()]
        .map(([date, period]) => ({ date, period }))
        .sort((a, b) => a.date.localeCompare(b.date));
    });
  });
  return { weeks: [...weekSet].sort(), byResource: combined };
}

/**
 * Remaining starts at the data date. Past weeks come from P6 / Maestro spent
 * so Past / All can show who was on site before that date.
 */
export function mergeRemainingWithActuals(remaining, actuals, dataDate) {
  const remBy = remaining?.byResource || {};
  const combinedActuals = combineActualCurves(actuals);
  const actBy = combinedActuals.byResource || {};
  const cut = dataDate ? String(dataDate).slice(0, 10) : null;
  const weekSet = new Set([...collectWeeks(remaining), ...combinedActuals.weeks]);
  const weeks = [...weekSet].sort();
  const actIndex = indexResources(actBy);

  const ids = new Set([...Object.keys(remBy), ...Object.keys(actBy)]);
  Object.values(remBy).forEach((r) => { if (r?.resourceId) ids.add(r.resourceId); });

  const usedAct = new Set();
  const byResource = {};
  ids.forEach((id) => {
    const rem = remBy[id];
    const act = actBy[id] || actIndex.get(id) || (rem?.resourceId ? actIndex.get(rem.resourceId) : null);
    if (!rem && !act) return;
    if (act) usedAct.add(act.resourceId || id);
    const remMap = new Map((rem?.series || []).map((p) => [String(p.date).slice(0, 10), Number(p.period) || 0]));
    const actMap = new Map((act?.series || []).map((p) => [String(p.date).slice(0, 10), Number(p.period) || 0]));
    if (cut) {
      let leftover = 0;
      for (const [w, h] of [...remMap.entries()]) {
        if (w < cut) {
          leftover += Number(h) || 0;
          remMap.delete(w);
        }
      }
      if (leftover) remMap.set(cut, (remMap.get(cut) || 0) + leftover);
    }
    const series = weeks.map((date) => {
      let period = 0;
      if (cut && date < cut) period = actMap.get(date) || 0;
      else if (cut) period = remMap.get(date) || 0;
      else period = remMap.has(date) ? (remMap.get(date) || 0) : (actMap.get(date) || 0);
      return { date, period };
    });
    if (!series.some((p) => p.period)) return;
    const meta = { ...(act || {}), ...(rem || {}) };
    const resourceId = rem?.resourceId || act?.resourceId || id;
    byResource[resourceId] = {
      ...meta,
      resourceId,
      resourceKey: rem?.resourceKey || act?.resourceKey || resourceId,
      resourceName: rem?.resourceName || act?.resourceName || resourceId,
      label: rem?.label || act?.label || rem?.resourceName || resourceId,
      discipline: rem?.discipline || act?.discipline || 'unknown',
      shopOrField: rem?.shopOrField || act?.shopOrField || 'field',
      isExtra: isExtraResource(meta),
      series
    };
  });

  Object.entries(actBy).forEach(([id, act]) => {
    const rid = act.resourceId || id;
    if (byResource[rid] || usedAct.has(rid)) return;
    const actMap = new Map((act.series || []).map((p) => [String(p.date).slice(0, 10), Number(p.period) || 0]));
    const series = weeks.map((date) => ({
      date,
      period: (!cut || date < cut) ? (actMap.get(date) || 0) : 0
    }));
    if (!series.some((p) => p.period)) return;
    byResource[rid] = { ...act, resourceId: rid, isExtra: isExtraResource(act), series };
  });

  return { weeks, byResource };
}

/**
 * P6 extract tables for the Staffing Plan tab:
 *   baselineHours — baseline schedule resource-assignment hours (6.1)
 *   baselineMen   — 6.1 ÷ P6 calendar(s) (6.2)
 *   curHours      — Current schedule: Actual Units (earned) to the data date, then
 *                   Remaining Units × FAC factor, optionally smoothed (6.3 + 6.5)
 *   curMen        — 6.3+6.5 ÷ P6 calendar(s) (6.4 + 6.6)
 *   hire          — week-over-week change in curMen (6.7 default — UI can override)
 *   diff          — curMen − baselineMen
 *
 * Men use each resource's own P6 calendar week hours (meta.weekHrCnt) via
 * weekHoursFn(date, meta); the caller decides fallbacks / what-if overrides.
 */
export function buildStaffingModel(opts = {}) {
  const {
    baseline, remaining, actuals, dataDate, filters = {}
  } = opts;
  const radius = opts.radius == null ? 0 : Math.max(0, Number(opts.radius) || 0);
  const factorN = Number(opts.forecastFactor);
  const factor = Number.isFinite(factorN) && factorN > 0 ? factorN : 1;
  const weekHoursFn = typeof opts.weekHoursFn === 'function'
    ? opts.weekHoursFn
    : () => Number(opts.weekHours) || 40;
  const cut = dataDate ? String(dataDate).slice(0, 10) : null;

  // Earned (P6 Actual Units) before the data date + Remaining after it, per resource.
  const cur = mergeRemainingWithActuals(remaining, actuals, cut);

  const baseBy = {};
  for (const [id, rec] of Object.entries(baseline?.byResource || {})) {
    if (!resourceMatchesFilters(rec, filters)) continue;
    baseBy[rec.resourceId || id] = rec;
  }
  const curBy = {};
  for (const [id, rec] of Object.entries(cur.byResource || {})) {
    if (!resourceMatchesFilters(rec, filters)) continue;
    curBy[rec.resourceId || id] = rec;
  }

  const weekSet = new Set(cur.weeks || []);
  Object.values(baseBy).forEach((rec) => (rec.series || []).forEach((p) => {
    if (p?.date) weekSet.add(String(p.date).slice(0, 10));
  }));
  let weeks = [...weekSet].sort();

  const ids = [...new Set([...Object.keys(baseBy), ...Object.keys(curBy)])];
  const rows = ids.map((rid) => {
    const b = baseBy[rid];
    const c = curBy[rid];
    const meta = { ...(b || {}), ...(c || {}) };
    const bMap = new Map((b?.series || []).map((p) => [String(p.date).slice(0, 10), Number(p.period) || 0]));
    const cMap = new Map((c?.series || []).map((p) => [String(p.date).slice(0, 10), Number(p.period) || 0]));
    return {
      meta,
      resourceId: rid,
      resourceKey: meta.resourceKey || rid,
      resourceName: meta.resourceName || rid,
      label: meta.label || meta.resourceName || rid,
      discipline: meta.discipline || 'unknown',
      shopOrField: meta.shopOrField || 'field',
      weekHrCnt: Number(meta.weekHrCnt) > 0 ? Number(meta.weekHrCnt) : null,
      isExtra: isExtraResource(meta),
      baselineHours: weeks.map((w) => bMap.get(w) || 0),
      curRawHours: weeks.map((w) => cMap.get(w) || 0)
    };
  });

  const activity = weeks.map((_, i) => rows.reduce(
    (s, r) => s + Math.abs(r.baselineHours[i]) + Math.abs(r.curRawHours[i]), 0
  ));
  const { first, last } = firstLastNonZero(activity);
  if (first >= 0 && last >= first) {
    weeks = weeks.slice(first, last + 1);
    rows.forEach((r) => {
      r.baselineHours = r.baselineHours.slice(first, last + 1);
      r.curRawHours = r.curRawHours.slice(first, last + 1);
    });
  } else {
    weeks = [];
    rows.forEach((r) => { r.baselineHours = []; r.curRawHours = []; });
  }

  let futureFrom = 0;
  if (cut) {
    const idx = weeks.findIndex((w) => w >= cut);
    futureFrom = idx < 0 ? weeks.length : idx;
  }

  const resources = rows.map((r) => {
    // Per-resource P6 calendar hours (weekHoursFn may apply what-if overrides per date)
    const hpw = weeks.map((d) => {
      const h = Number(weekHoursFn(d, r.meta));
      return Number.isFinite(h) && h > 0 ? h : 40;
    });
    const scaled = r.curRawHours.map((h, i) => (i >= futureFrom ? h * factor : h));
    const curHours = scaled.map((h, i) => (
      radius > 0 && i >= futureFrom
        ? round2(centeredAverageRange(scaled, i, radius, futureFrom, scaled.length - 1))
        : round2(h)
    ));
    const curMen = curHours.map((h, i) => roundMen(hoursToMen(h, hpw[i])));
    const baselineMen = r.baselineHours.map((h, i) => roundMen(hoursToMen(h, hpw[i])));
    const hire = hireLayoff(curMen);
    const diff = curMen.map((m, i) => m - baselineMen[i]);
    const peakMen = curMen.reduce((m, v) => Math.max(m, v), 0);
    return {
      resourceId: r.resourceId,
      resourceKey: r.resourceKey,
      resourceName: r.resourceName,
      label: r.label,
      discipline: r.discipline,
      shopOrField: r.shopOrField,
      weekHrCnt: r.weekHrCnt,
      isExtra: r.isExtra,
      hpw,
      baselineHours: r.baselineHours.map(round2),
      curHours,
      baselineMen,
      curMen,
      hire,
      diff,
      peakMen,
      totalBaselineHours: round2(r.baselineHours.reduce((s, v) => s + v, 0)),
      totalCurHours: round2(curHours.reduce((s, v) => s + v, 0))
    };
  }).filter((r) => r.totalBaselineHours !== 0 || r.totalCurHours !== 0);

  resources.sort((a, b) => {
    const da = `${a.discipline}|${a.shopOrField}|${a.resourceId}`;
    const db = `${b.discipline}|${b.shopOrField}|${b.resourceId}`;
    return da.localeCompare(db);
  });

  const totals = {
    baselineHours: weeks.map((_, i) => round2(resources.reduce((s, r) => s + (r.baselineHours[i] || 0), 0))),
    baselineMen: weeks.map((_, i) => resources.reduce((s, r) => s + (r.baselineMen[i] || 0), 0)),
    curHours: weeks.map((_, i) => round2(resources.reduce((s, r) => s + (r.curHours[i] || 0), 0))),
    curMen: weeks.map((_, i) => resources.reduce((s, r) => s + (r.curMen[i] || 0), 0))
  };
  totals.hire = hireLayoff(totals.curMen);
  totals.diff = totals.curMen.map((m, i) => m - totals.baselineMen[i]);

  const peakCur = totals.curMen.reduce((m, v) => Math.max(m, v), 0);
  const peakBaseline = totals.baselineMen.reduce((m, v) => Math.max(m, v), 0);

  return {
    radius,
    windowWeeks: radius * 2 + 1,
    factor,
    weeks,
    cutIndex: futureFrom,
    resources,
    totals,
    summary: {
      peakCurMen: peakCur,
      peakCurWeek: weeks[totals.curMen.indexOf(peakCur)] || null,
      peakBaselineMen: peakBaseline,
      peakBaselineWeek: weeks[totals.baselineMen.indexOf(peakBaseline)] || null,
      resourceCount: resources.length
    }
  };
}

function alignResourceHours(byResource, weeks) {
  const rows = [];
  for (const [id, rec] of Object.entries(byResource || {})) {
    const byDate = new Map((rec.series || []).map((pt) => [pt.date, Number(pt.period) || 0]));
    const hours = weeks.map((w) => byDate.get(w) || 0);
    rows.push({
      resourceId: rec.resourceId || id,
      resourceName: rec.resourceName || rec.rsrc_name || id,
      resourceKey: rec.resourceKey || id,
      discipline: rec.discipline || 'unknown',
      shopOrField: rec.shopOrField || 'field',
      isExtra: isExtraResource(rec),
      label: rec.label || rec.resourceName || rec.resourceId || id,
      hours
    });
  }
  rows.sort((a, b) => {
    const da = `${a.discipline}|${a.shopOrField}|${a.resourceId}`;
    const db = `${b.discipline}|${b.shopOrField}|${b.resourceId}`;
    return da.localeCompare(db);
  });
  return rows;
}

export function buildStaffingPlan(remaining, options = {}) {
  const radius = options.radius == null ? DEFAULT_RADIUS : Math.max(0, Number(options.radius) || 0);
  const weekHoursFn = typeof options.weekHoursFn === 'function'
    ? options.weekHoursFn
    : () => Number(options.weekHours) || 40;
  const dataDate = options.dataDate ? String(options.dataDate).slice(0, 10) : null;

  const rawWeeks = Array.isArray(remaining?.weeks) && remaining.weeks.length
    ? remaining.weeks.slice()
    : [...new Set(
      Object.values(remaining?.byResource || {}).flatMap((r) => (r.series || []).map((p) => p.date))
    )].sort();

  let weeks = dataDate ? rawWeeks.filter((w) => w >= dataDate) : rawWeeks.slice();

  const filteredByResource = {};
  for (const [id, rec] of Object.entries(remaining?.byResource || {})) {
    if (!resourceMatchesFilters(rec, options.filters || {})) continue;
    filteredByResource[id] = rec;
  }

  const aligned = alignResourceHours(filteredByResource, weeks);
  if (!aligned.length) weeks = [];

  const weekHoursFull = weeks.map((d) => {
    const h = Number(weekHoursFn(d));
    return Number.isFinite(h) && h > 0 ? h : 40;
  });

  const built = aligned.map((r) => {
    const planHours = smoothSeries(r.hours, radius).map(round2);
    const needMen = r.hours.map((h, i) => roundMen(hoursToMen(h, weekHoursFull[i])));
    const planMen = planHours.map((h, i) => roundMen(hoursToMen(h, weekHoursFull[i])));
    return { ...r, planHours, needMen, planMen };
  });

  const activity = weeks.map((_, i) => (
    built.reduce((s, r) => s + Math.abs(r.hours[i] || 0) + Math.abs(r.planHours[i] || 0), 0)
  ));
  const { first, last } = firstLastNonZero(activity);
  if (first >= 0 && last >= first) {
    weeks = weeks.slice(first, last + 1);
    built.forEach((r) => {
      r.hours = r.hours.slice(first, last + 1);
      r.planHours = r.planHours.slice(first, last + 1);
      r.needMen = r.needMen.slice(first, last + 1);
      r.planMen = r.planMen.slice(first, last + 1);
    });
  } else {
    weeks = [];
    built.forEach((r) => {
      r.hours = [];
      r.planHours = [];
      r.needMen = [];
      r.planMen = [];
    });
  }

  const weekHours = weekHoursFull.slice(Math.max(0, first), last >= 0 ? last + 1 : 0);

  const resources = built.map((r) => {
    const hire = hireLayoff(r.planMen);
    const variance = r.planMen.map((p, i) => p - r.needMen[i]);
    const peakMen = r.planMen.reduce((m, v) => Math.max(m, v), 0);
    const peakIdx = r.planMen.indexOf(peakMen);
    return {
      resourceId: r.resourceId,
      resourceKey: r.resourceKey,
      resourceName: r.resourceName,
      label: r.label,
      discipline: r.discipline,
      shopOrField: r.shopOrField,
      isExtra: r.isExtra,
      family: familyFromResource(r),
      needHours: r.hours.map(round2),
      planHours: r.planHours,
      needMen: r.needMen,
      planMen: r.planMen,
      hire,
      variance,
      peakMen,
      peakWeek: peakIdx >= 0 ? weeks[peakIdx] : null,
      totalNeedHours: round2(r.hours.reduce((s, v) => s + v, 0)),
      totalPlanHours: round2(r.planHours.reduce((s, v) => s + v, 0))
    };
  }).filter((r) => r.totalNeedHours !== 0 || r.totalPlanHours !== 0);

  const totals = {
    needHours: weeks.map((_, i) => round2(resources.reduce((s, r) => s + (r.needHours[i] || 0), 0))),
    planHours: weeks.map((_, i) => round2(resources.reduce((s, r) => s + (r.planHours[i] || 0), 0))),
    needMen: weeks.map((_, i) => resources.reduce((s, r) => s + (r.needMen[i] || 0), 0)),
    planMen: weeks.map((_, i) => resources.reduce((s, r) => s + (r.planMen[i] || 0), 0))
  };
  totals.hire = hireLayoff(totals.planMen);
  totals.variance = totals.planMen.map((p, i) => p - totals.needMen[i]);

  const peakPlan = totals.planMen.reduce((m, v) => Math.max(m, v), 0);
  const peakNeed = totals.needMen.reduce((m, v) => Math.max(m, v), 0);

  let hireNext4 = 0;
  let layoffNext4 = 0;
  totals.hire.slice(0, 4).forEach((d) => {
    if (d > 0) hireNext4 += d;
    if (d < 0) layoffNext4 += -d;
  });

  let biggestGap = null;
  resources.forEach((r) => {
    r.variance.forEach((v, i) => {
      if (!biggestGap || Math.abs(v) > Math.abs(biggestGap.men)) {
        biggestGap = { resourceId: r.resourceId, label: r.label, week: weeks[i], men: v };
      }
    });
  });

  return {
    radius,
    windowWeeks: radius * 2 + 1,
    weeks,
    weekHours,
    resources,
    totals,
    summary: {
      peakPlanMen: peakPlan,
      peakPlanWeek: weeks[totals.planMen.indexOf(peakPlan)] || null,
      peakNeedMen: peakNeed,
      peakNeedWeek: weeks[totals.needMen.indexOf(peakNeed)] || null,
      hireNext4,
      layoffNext4,
      biggestGap,
      resourceCount: resources.length,
      totalNeedHours: round2(totals.needHours.reduce((s, v) => s + v, 0)),
      totalPlanHours: round2(totals.planHours.reduce((s, v) => s + v, 0))
    }
  };
}

export const SUPER_CFG_KEY = 'evm-supervision-cfg-v1';
export const STAFFING_ADJUST_KEY = 'evm-staffing-adjust-v1';

export function loadSuperCfg(jobNumber) {
  try {
    const raw = JSON.parse(localStorage.getItem(`${SUPER_CFG_KEY}-${jobNumber}`) || 'null');
    if (raw && typeof raw === 'object') {
      return {
        from: raw.from || '',
        to: raw.to || '',
        overrides: raw.overrides && typeof raw.overrides === 'object' ? raw.overrides : {}
      };
    }
  } catch { /* ignore */ }
  return { from: '', to: '', overrides: {} };
}

export function saveSuperCfg(jobNumber, cfg) {
  try { localStorage.setItem(`${SUPER_CFG_KEY}-${jobNumber}`, JSON.stringify(cfg)); } catch { /* ignore */ }
}

export function loadStaffingAdjust(jobNumber) {
  try {
    const raw = JSON.parse(localStorage.getItem(`${STAFFING_ADJUST_KEY}-${jobNumber}`) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch { return {}; }
}

/** Rebuild forecast men from Hiring / layoff overrides (same as the Staffing tab). */
export function applyStaffingAdjust(model, adjust = {}) {
  const menBy = new Map();
  (model?.resources || []).forEach((r) => {
    const men = (r.curMen || []).slice();
    const cut = Math.max(model.cutIndex, 0);
    for (let i = cut; i < men.length; i += 1) {
      const o = adjust[`${r.resourceId}|${model.weeks[i]}`];
      const defH = (r.curMen[i] || 0) - (i > 0 ? (r.curMen[i - 1] || 0) : 0);
      const h = o == null ? defH : (Number(o) || 0);
      men[i] = (i > 0 ? men[i - 1] : 0) + h;
    }
    menBy.set(r.resourceId, men);
  });
  return menBy;
}
